import { companyGroupLabel } from '@/lib/company-group';
import type { VisaTutorClientRow, VisaTutorCountryRow } from '@/lib/types';

/**
 * Pure helpers for the AI Visa Tutor page. Nothing here fetches or renders.
 *
 * - A client row is a school or a university that bought licences. Its group
 *   (Institutions / Partners) is the EMERGE company's list group when the row
 *   resolved to one; an unmatched row is an institution, since both upstream
 *   entity types are.
 * - `upstreamCompanyId` is the SEED platform id, never `d_companies.id`;
 *   links go through `company` only.
 * - Averages are `null` when nothing was scored and render as an em dash,
 *   never as 0.
 */

const ONE_DECIMAL = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Scales confirmed with the SEED platform (2026-10-18): every session score
 * (overall, communication, body language, visa, first, latest, best) is 0–100;
 * `avgRating` is 1–5. The scale is part of the figure everywhere it shows.
 */
const SCORE_SCALE = 100;
const RATING_SCALE = 5;

function formatOutOf(value: number | null | undefined, scale: number): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${ONE_DECIMAL.format(value)} / ${scale}`;
}

/** A 0–100 session score as "70.3 / 100"; null renders "—". */
export function formatScore(value: number | null | undefined): string {
  return formatOutOf(value, SCORE_SCALE);
}

/** A 1–5 rating as "4.2 / 5"; null renders "—". */
export function formatRating(value: number | null | undefined): string {
  return formatOutOf(value, RATING_SCALE);
}

/** An upstream percentage (12.3 = 12.3%), signed; null renders "—". */
export function formatSignedPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const text = `${ONE_DECIMAL.format(Math.abs(value))}%`;
  if (value > 0) return `+${text}`;
  if (value < 0) return `−${text}`;
  return text;
}

/* --- groups --------------------------------------------------------------- */

export interface ClientGroup {
  label: string;
  rows: VisaTutorClientRow[];
}

function groupInfo(row: VisaTutorClientRow): { label: string; order: number } {
  if (row.company?.group) {
    return { label: companyGroupLabel(row.company.group.name), order: row.company.group.sortOrder };
  }
  return { label: 'Institutions', order: -1 };
}

/** Buckets rows by group (lowest order first), keeping each bucket's row order. */
export function splitClientGroups(rows: readonly VisaTutorClientRow[]): ClientGroup[] {
  const byLabel = new Map<string, { label: string; order: number; rows: VisaTutorClientRow[] }>();
  for (const row of rows) {
    const info = groupInfo(row);
    const bucket = byLabel.get(info.label);
    if (bucket === undefined) byLabel.set(info.label, { ...info, rows: [row] });
    else {
      bucket.rows.push(row);
      bucket.order = Math.min(bucket.order, info.order);
    }
  }
  return [...byLabel.values()]
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
    .map(({ label, rows: bucketRows }) => ({ label, rows: bucketRows }));
}

/** A stable React key for a client row. */
export function clientKey(row: VisaTutorClientRow, index: number): string {
  if (row.schoolId !== null) return `s:${row.schoolId}`;
  if (row.universityId !== null) return `u:${row.universityId}`;
  if (row.upstreamCompanyId !== null) return `p:${row.upstreamCompanyId}`;
  return `i:${index}`;
}

/** The second line under a client's name: its university, when it is a school. */
export function clientSubtitle(row: VisaTutorClientRow): string | null {
  if (row.entityType !== 'school') return null;
  const university = row.universityName?.trim() ?? '';
  if (university === '' || university === row.name.trim()) return null;
  return university;
}

export function matchesClientSearch(row: VisaTutorClientRow, needle: string): boolean {
  if (needle === '') return true;
  return (
    row.name.toLowerCase().includes(needle) ||
    (row.universityName?.toLowerCase().includes(needle) ?? false) ||
    (row.company != null && row.company.name.toLowerCase().includes(needle))
  );
}

/* --- countries ------------------------------------------------------------ */

export function countryLabel(row: VisaTutorCountryRow): string {
  const name = row.country?.trim() ?? '';
  if (name !== '') return name;
  const code = row.countryCode?.trim() ?? '';
  return code !== '' ? code : 'Not recorded';
}

export interface DestinationOption {
  code: string;
  label: string;
}

/**
 * Destination choices for the toolbar: rows with an ISO code, by label. The
 * `selected` code is kept as an option even when the rows no longer carry it.
 */
export function destinationOptions(
  rows: readonly VisaTutorCountryRow[],
  selected: string | null,
): DestinationOption[] {
  const byCode = new Map<string, DestinationOption>();
  for (const row of rows) {
    const code = row.countryCode?.trim().toUpperCase() ?? '';
    if (!/^[A-Z]{2}$/.test(code) || byCode.has(code)) continue;
    byCode.set(code, { code, label: countryLabel(row) });
  }
  if (selected !== null && !byCode.has(selected)) byCode.set(selected, { code: selected, label: selected });
  return [...byCode.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/* --- the "Synced" data fact ----------------------------------------------- */

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });
const DATE_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** "10:42" today, "17 Oct, 10:42" otherwise; null for a missing or unparseable instant. */
export function syncedText(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined || iso === '') return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  return (sameDay ? TIME_FORMAT : DATE_TIME_FORMAT).format(date);
}
